export interface WorkshopFormat {
  id: string;
  icon: "compass" | "spark" | "bot" | "puzzle";
  label: string;
  title: string;
  subtitle: string;
  summary: string;
  outcomes: string[];
  pdfUrl?: string;
}

export interface WorkshopFacilitator {
  name: string;
  role: string;
  bio: string;
  image: string;
  links: {
    label: string;
    href: string;
  }[];
}

export interface WorkshopFaq {
  question: string;
  answerHtml: string;
}

export interface WorkshopTutor {
  name: string;
  title: string;
}

export const trustedLogos = [
  {
    name: "HSBC",
    image: require("../../../../../assets/images/workshops/hsbc-logo.svg"),
  },
  {
    name: "Ogilvy",
    image: require("../../../../../assets/images/workshops/ogilvy-logo.svg"),
  },
  {
    name: "PwC",
    image: require("../../../../../assets/images/workshops/pwc-logo.svg"),
  },
  {
    name: "Omnicom",
    image: require("../../../../../assets/images/workshops/omnicom.svg"),
  },
];

export const workshopFormats: WorkshopFormat[] = [
  {
    id: "ai-leaders",
    icon: "compass",
    label: "For managers",
    title: "AI Leaders Workshop",
    subtitle: 'From "Oh no" to "Let\'s go!"',
    summary:
      "For team leads and managers who need to lead AI adoption without becoming the office tech support.",
    outcomes: [
      "A jargon-free grasp of what AI actually does well (and badly)",
      "Practical automations your team can run with by Friday",
      "A 30, 60, 90-day plan to roll AI out responsibly",
    ],
    pdfUrl:
      "https://42courses-course-material.s3.us-west-1.amazonaws.com/ai-leaders-workshop.pdf",
  },
  {
    id: "ai-vibe-marketing",
    icon: "spark",
    label: "For brand teams",
    title: "AI Vibe Marketing",
    subtitle: "How to use AI without losing your brand's soul.",
    summary:
      "For marketers tired of AI output that sounds like every other AI output. Keep the speed, keep the voice.",
    outcomes: [
      "The VIBE framework for blending human craft with AI scale",
      "Hands-on with the best tools for copy, image and video",
      "A repeatable mini-campaign process you can use tomorrow",
    ],
    pdfUrl:
      "https://42courses-course-material.s3.us-west-1.amazonaws.com/ai-vibe-marketing-workshop.pdf",
  },
  {
    id: "ai-prototyping",
    icon: "bot",
    label: "For builders",
    title: "AI Prototyping",
    subtitle: "Build practical agents and bots that work.",
    summary:
      "For teams who want to stop talking about agents and start shipping them. No-code and low-code, end to end.",
    outcomes: [
      "Demystify agents through real, working examples",
      "Build your own bot in the room, from idea to demo",
      "Spot the highest-impact automations in your own workflow",
    ],
    pdfUrl:
      "https://42courses-course-material.s3.us-west-1.amazonaws.com/ai-prototyping-workshop.pdf",
  },
  {
    id: "custom",
    icon: "puzzle",
    label: "Bespoke",
    title: "Custom Programmes",
    subtitle: "When off-the-shelf doesn't fit.",
    summary:
      "We built a two-day programme for O&L Group spanning creative, ops, events and finance. Every team got a workflow, a prompt library and a system that worked from day one.",
    outcomes: [
      "Mapped to your real workflows, not generic use cases",
      "Custom prompt libraries and workflows your team owns",
      "Multi-team format, optional follow-up coaching",
    ],
  },
];

export const facilitators: WorkshopFacilitator[] = [
  {
    name: "Chris Rawlinson",
    role: "Founder, 42courses",
    image: require("../../../../../assets/images/workshops/chris-rawlinson-bio-pic.jpg"),
    bio:
      "Educator, innovator, entrepreneur. Built 42courses with partners including Cannes Lions. Looked after innovation at Ogilvy and WPP. Mentor for Google and Techstars.",
    links: [
      {
        label: "LinkedIn",
        href: "https://www.linkedin.com/in/christopherrawlinson/",
      },
    ],
  },
  {
    name: "Dave Duarte",
    role: "Founder, Treeshake",
    image: require("../../../../../assets/images/workshops/dave-duarte-bio-pic.jpg"),
    bio:
      "Speaker, entrepreneur, campaigner. Has driven campaigns reaching 1bn+ people in 150+ countries. WEF Young Global Leader. Spoken at Davos, the UN and conferences in 25 countries.",
    links: [
      {
        label: "LinkedIn",
        href: "https://www.linkedin.com/in/daveduarte/",
      },
      {
        label: "Website",
        href: "https://www.daveduarte.net/",
      },
    ],
  },
  {
    name: "Conor McCarthy",
    role: "Founder, Threshold Partners",
    image: require("../../../../../assets/images/workshops/conor-mccarthy-bio-pic.png"),
    bio:
      "Builder, operator, fractional AI lead. 20 years building software and companies. Runs a free arts festival drawing 250,000 people a year. Author of The Unstuck Book and coach via Seth Godin's Akimbo.",
    links: [
      {
        label: "LinkedIn",
        href: "https://www.linkedin.com/in/comccart/",
      },
      {
        label: "Website",
        href: "https://www.conormccarthy.me/",
      },
    ],
  },
];

export const tutorRows: WorkshopTutor[][] = [
  [
    { name: "Rory Sutherland", title: "Vice Chairman, Ogilvy" },
    { name: "Dan Ariely", title: "Professor, Duke" },
    { name: "Sir John Hegarty", title: "Founder, BBH" },
    { name: "Dave Trott", title: "Creative legend" },
    { name: "Les Binet", title: "Effectiveness expert" },
    { name: "Cindy Gallop", title: "Founder, MakeLoveNotPorn" },
    { name: "Mark Ritson", title: "Marketing Professor" },
    { name: "Meik Wiking", title: "CEO, Happiness Research" },
  ],
  [
    { name: "Josy Paul", title: "Chairman, BBDO India" },
    { name: "Keith Reinhard", title: "Chairman Emeritus, DDB" },
    { name: "Vicky Ross", title: "Copywriting" },
    { name: "Steve Harrison", title: "Copywriting" },
    { name: "Jules Goddard", title: "Strategy, LBS" },
    { name: "Faris Yakob", title: "Strategy" },
    { name: "Phil Adams", title: "Planning" },
    { name: "Shaheed Peera", title: "Cannes Lions" },
  ],
  [
    { name: "Tom Goodwin", title: "Innovation" },
    { name: "Helen Edwards", title: "Branding" },
    { name: "Richard Shotton", title: "Behavioural Science" },
    { name: "Nir Eyal", title: "Behavioural Design" },
    { name: "Bruce Daisley", title: "Workplace Culture" },
    { name: "Caroline Webb", title: "Behavioural Science" },
    { name: "Tom Roach", title: "Effectiveness, BBH" },
    { name: "Orlando Wood", title: "System1" },
  ],
];

export const faqs: WorkshopFaq[] = [
  {
    question: "How much does a workshop cost?",
    answerHtml:
      'Pricing depends on the format, the size of the team and how much customisation you\'d like. Half-day workshops start at one set rate, multi-day custom programmes are scoped individually. Tell us a bit about what you\'re after in the <a href="#enquire">enquiry form</a> and we\'ll come back with a tailored proposal within one working day. No surprises, no hidden extras.',
  },
  {
    question: "How far in advance do we need to book?",
    answerHtml:
      "<strong>One week minimum, ideally two or three.</strong> The week is the bare minimum because that's how long it takes us to do the prep work that makes the difference. We'll send you a short discovery questionnaire, run a 30-minute call with whoever's commissioning the workshop, gather example briefs or assets your team actually works on, and bake all of it into the session. Generic AI training is everywhere. Workshops that feel like they were built for your team specifically take a bit of runway.",
  },
  {
    question: "How big can the group be?",
    answerHtml:
      "Bigger than you'd think. The sweet spot is 8 to 20 people, where everyone gets hands-on time and the room stays conversational. But we've run sessions for <strong>70+ at a time</strong>, breaking the room into smaller teams for the hands-on parts so nobody ends up watching from the back. If your group is unusually large or oddly shaped, just tell us. We'll design around it.",
  },
  {
    question: "Do you run workshops remotely or only in person?",
    answerHtml:
      "Honestly, we'd always recommend in person if it's possible. The energy in the room, the side-conversations during exercises, the moment someone in marketing realises someone in ops has solved their exact problem with a prompt, that stuff is hard to recreate over Zoom. <strong>That said, we're flexible.</strong> If remote is the only way, we'll make it work and design it carefully so the session is still effective. It just takes a bit more planning. We've delivered in 25+ countries, so distance is rarely a barrier either way.",
  },
  {
    question: "What's actually different about a 42courses workshop?",
    answerHtml:
      "<p>The big one: <strong>we leave a working framework in place, agreed with leadership before we go.</strong> Most training evaporates the week after, because nothing was set up to keep the momentum. We make sure something concrete continues, owned by someone, with leadership backing it.</p><p>On top of that: we prep properly around your real workflows rather than running generic AI content, your team builds something they can use the moment they're back at their desks, and the people teaching it have shipped this work in agencies, brands and start-ups. Educators who've built things, not consultants who turned up to talk about them.</p>",
  },
  {
    question: "Can you train teams outside the UK?",
    answerHtml:
      "Yes. We've delivered workshops in 25+ countries, both remotely and in person. For international in-person work we'll factor travel into the proposal, but it's rarely a dealbreaker. We've done sessions across Europe, Africa, North America, the Middle East and Asia-Pacific.",
  },
  {
    question: "Can you arrange a keynote or thought leader instead of a workshop?",
    answerHtml:
      'Yes. Alongside the workshops, we can arrange keynote talks, masterclasses or copywriting deep-dives with selected tutors from our 60-strong course faculty. Some are represented by agents and have lead times of their own, so the earlier you ask the better. Just mention what you have in mind in the <a href="#enquire">enquiry form</a> and we\'ll see what we can do.',
  },
];
